import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { bazaarApi, type BazaarEvent } from '../api/endpoints';
import { useOrganizationsState } from '../hooks/useOrganizations';
import { STATUS_LABELS } from '../format';
import { useNavContext } from './navContext';

/**
 * Umschalter neben dem Logo: zeigt die aktuelle Organisation bzw. den aktuellen Basar und klappt eine Liste der
 * eigenen Organisationen und der Basare der aktuellen Organisation auf. Er erscheint nur auf Seiten einer
 * Organisation oder eines Basars; die Listen kommen von den vorhandenen Endpunkten (`GET /orgs`, `GET /orgs/:id/events`).
 */
export function ContextSwitcher() {
  const location = useLocation();
  const context = useNavContext();
  const orgMatch = /^\/organisationen\/([^/]+)/.exec(location.pathname);
  const eventMatch = /^\/basare\/([^/]+)/.exec(location.pathname);
  const eventContext = eventMatch !== null && context.eventId === eventMatch[1] ? context : null;
  const orgId = orgMatch?.[1] ?? eventContext?.orgId ?? null;
  const visible = orgId !== null;

  const { orgs, failed: orgsFailed } = useOrganizationsState(visible);
  /** Helfer und Kassierer sind keine Mitglieder der Organisation: dann nur der aktuelle Basar, keine Listen. */
  const isMember = orgs?.some((org) => org.id === orgId) === true;
  const notMember = orgs !== null && !isMember;
  const [openFor, setOpenFor] = useState<string | null>(null);
  const open = openFor === location.pathname;
  const setOpen = (value: boolean | ((current: boolean) => boolean)) => {
    const next = typeof value === 'function' ? value(open) : value;
    setOpenFor(next ? location.pathname : null);
  };
  const [events, setEvents] = useState<{
    orgId: string;
    list: BazaarEvent[] | null;
  } | null>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || orgId === null || !isMember) return;
    const guard = { cancelled: false };
    bazaarApi.listEvents(orgId).then(
      (list) => {
        if (!guard.cancelled) setEvents({ orgId, list });
      },
      () => {
        if (!guard.cancelled) setEvents({ orgId, list: null });
      },
    );
    return () => {
      guard.cancelled = true;
    };
  }, [open, orgId, isMember]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenFor(null);
    };
    const onPointer = (event: PointerEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpenFor(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  if (!visible) return null;
  const currentOrg = orgs?.find((org) => org.id === orgId);
  const orgLabel = currentOrg?.name ?? eventContext?.orgName ?? 'Organisation';
  const label = eventContext?.eventTitle ?? orgLabel;
  const loadedEvents = events !== null && events.orgId === orgId ? events : null;
  const eventList = loadedEvents?.list ?? null;
  const eventsFailed = loadedEvents !== null && loadedEvents.list === null;

  return (
    <div
      className="switcher"
      ref={root}
      onBlur={(event) => {
        const next = event.relatedTarget;
        // Safari setzt bei Klicks auf Knöpfe keinen Fokus (relatedTarget ist null): dort nur Escape und Klick außerhalb
        if (next === null) return;
        if (open && !(next instanceof Node && event.currentTarget.contains(next))) setOpen(false);
      }}
    >
      <button
        type="button"
        className="switcher__button"
        aria-expanded={open}
        aria-controls={open ? 'switcher-panel' : undefined}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <span className="switcher__label">{label}</span>
        <span aria-hidden="true">▾</span>
        <span className="visually-hidden"> Organisation oder Basar wechseln</span>
      </button>
      {open && (
        <div className="switcher__panel" id="switcher-panel">
          {notMember ? (
            <ul>
              <li>
                {eventContext === null ? (
                  <span className="muted">Kein weiterer Bereich verfügbar.</span>
                ) : (
                  <Link to={`/basare/${eventContext.eventId ?? ''}`} aria-current="page">
                    {eventContext.eventTitle}
                  </Link>
                )}
              </li>
            </ul>
          ) : (
            <>
              <p className="switcher__heading">Ihre Organisationen</p>
              <ul>
                {(orgs ?? []).map((org) => (
                  <li key={org.id}>
                    <Link
                      to={`/organisationen/${org.id}`}
                      {...(org.id === orgId && eventContext === null
                        ? { 'aria-current': 'page' }
                        : {})}
                    >
                      {org.name}
                    </Link>
                  </li>
                ))}
                {orgs === null && !orgsFailed && <li className="muted">Wird geladen …</li>}
                {orgsFailed && (
                  <li className="muted" role="alert">
                    Organisationen konnten nicht geladen werden.
                  </li>
                )}
              </ul>
              <p className="switcher__heading">Basare von {orgLabel}</p>
              <ul>
                {(eventList ?? []).map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/basare/${item.id}`}
                      {...(item.id === eventContext?.eventId ? { 'aria-current': 'page' } : {})}
                    >
                      {item.title}
                      <span className="muted"> · {STATUS_LABELS[item.status]}</span>
                    </Link>
                  </li>
                ))}
                {eventList === null && !eventsFailed && !orgsFailed && (
                  <li className="muted">Wird geladen …</li>
                )}
                {eventsFailed && (
                  <li className="muted" role="alert">
                    Basare konnten nicht geladen werden.
                  </li>
                )}
                {eventList?.length === 0 && <li className="muted">Noch keine Basare.</li>}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
